'use client';

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@clerk/clerk-react';
import { MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import LoadingSpinner from '@/components/LoadingSpinner';

interface CharacterChat {
  id: string;
  characterName: string;
  docTitle: string;
}

export default function CharacterChats() {
  const { isLoaded, isSignedIn } = useAuth();
  const { apiCall } = useAuthenticatedApi();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [chats, setChats] = useState<CharacterChat[]>([]);
  const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
  const [chatToDelete, setChatToDelete] = useState<CharacterChat | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/signin');
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (isSignedIn) {
      fetchChats();
    }
  }, [isSignedIn]);

  const fetchChats = async () => {
    setIsLoading(true);
    try {
      // The API returns an array directly, not wrapped in a 'chats' property
      const response = await apiCall<CharacterChat[]>('/list-character-doc-chats', 'POST');

      if (response && Array.isArray(response)) {
        setChats(response);
      } else {
        setChats([]);
      }
    } catch (error) {
      console.error('Error fetching character chats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load character chats. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (chat: CharacterChat) => {
    setChatToDelete(chat);
  };

  const cancelDelete = () => {
    setChatToDelete(null);
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    const id = chatToDelete.id;
    // Update the deleting state for this specific chat
    setIsDeleting((prev) => ({ ...prev, [id]: true }));

    try {
      await apiCall(`/character-doc-chat?id=${id}`, 'DELETE');

      // Remove the deleted chat from the state
      setChats(chats.filter((chat) => chat.id !== id));

      toast({
        title: 'Success',
        description: 'Character chat deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting character chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete character chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      // Reset the deleting state for this chat
      setIsDeleting((prev) => ({ ...prev, [id]: false }));
      // Close the dialog
      setChatToDelete(null);
    }
  };

  if (!isLoaded || !isSignedIn) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto py-6 px-4 md:px-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Character Document Chats</h1>
            <p className="text-muted-foreground">View and manage your character conversations</p>
          </div>
          <Button onClick={() => navigate('/documents')} className="ml-auto">
            New Chat
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : chats.length === 0 ? (
          <Card className="w-full">
            <CardHeader>
              <CardTitle>No Character Chats</CardTitle>
              <CardDescription>You haven't created any character chats yet.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center py-8">Start a new character chat to begin a conversation.</p>
            </CardContent>
            <CardFooter>
              <Button onClick={() => navigate('/character-doc-chat')} className="w-full">
                Create New Chat
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chats.map((chat) => (
              <Card key={chat.id} className="flex flex-col">
                <CardHeader>
                  <CardTitle className="truncate">{chat.characterName}</CardTitle>
                  <CardDescription>{chat.docTitle}</CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="flex items-center justify-center h-full">
                    <MessageSquare className="h-16 w-16 text-muted-foreground/30" />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline" size="sm" asChild className="flex-1 mr-2">
                    <Link to={`/character-doc-chat?id=${chat.id}`}>Open Chat</Link>
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDelete(chat)}
                    disabled={isDeleting[chat.id]}
                  >
                    {isDeleting[chat.id] ? (
                      <div className="h-4 w-4">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!chatToDelete} onOpenChange={(open) => !open && cancelDelete()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Character Chat</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the chat with {chatToDelete?.characterName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteChat}
              disabled={chatToDelete ? isDeleting[chatToDelete.id] : false}
            >
              {chatToDelete && isDeleting[chatToDelete.id] ? (
                <>
                  <div className="h-4 w-4 mr-2">
                    <LoadingSpinner />
                  </div>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
